// Phase SA-7 — Shared types + pure helpers for slip_autopsy_legs.
//
// Mirrors the SA-2 migration column shape exactly. The rows are
// populated by SA-6's Edge fn upgrade; SA-7 only ships the
// renderer + pure derivations. SA-8 builds the hook that reads
// the rows for a given autopsy.

/** The 6 verdict values pinned by the SA-2 migration CHECK. */
export type Verdict =
  | "PROCESS_CORRECT"
  | "BELOW_THRESHOLD"
  | "MODEL_ERROR"
  | "VOID"
  | "WON_NO_DATA"
  | "LOST_NO_DATA";

/** Per-leg outcome. Note: 'pending' is a transient state pre-settlement. */
export type LegOutcome = "won" | "lost" | "void" | "pending" | "unknown";

/** Confidence tier — coarse bucket that's safe for free-tier display. */
export type ConfidenceTier = "HIGH" | "SOLID" | "VALUE" | "WEAK";

/** One row from public.slip_autopsy_legs. */
export interface SlipAutopsyLegRow {
  leg_id: string;
  autopsy_id: string;
  user_id: string;
  leg_order: number;
  fixture_name: string;
  market: string;
  selection: string;
  odds: number | null;
  outcome: LegOutcome | null;
  /** Numeric 0-100 confidence. Pro users see it; free see dots. */
  confidence_score: number | null;
  confidence_tier: ConfidenceTier | null;
  verdict: Verdict | null;
  reasoning: string | null;
  /** Populated only for MODEL_ERROR verdicts. */
  learning_applied: string | null;
  was_in_strikza_pipeline: boolean;
  pattern_tags: string[];
  created_at: string;
}

/** Pure: confidence dot tier (free-user display). Three dots: 3
 * filled = HIGH, 2 = SOLID, 1 = VALUE, 0 = WEAK / null. */
export function confidenceToDots(
  score: number | null,
  tier: ConfidenceTier | null,
): { dots: 0 | 1 | 2 | 3; tier: ConfidenceTier } {
  // Prefer the stored tier if set (SA-6's classifier may write
  // tier directly); else derive from score.
  if (tier) {
    const derivedDots: Record<ConfidenceTier, 0 | 1 | 2 | 3> = {
      HIGH: 3,
      SOLID: 2,
      VALUE: 1,
      WEAK: 0,
    };
    return { dots: derivedDots[tier], tier };
  }
  if (score == null || !Number.isFinite(score)) {
    return { dots: 0, tier: "WEAK" };
  }
  if (score >= 80) return { dots: 3, tier: "HIGH" };
  if (score >= 65) return { dots: 2, tier: "SOLID" };
  if (score >= 50) return { dots: 1, tier: "VALUE" };
  return { dots: 0, tier: "WEAK" };
}

/** Pure: short, human label per verdict — used in the collapsed
 * row + one-line expanded summary. */
export function verdictLabel(v: Verdict | null): string {
  switch (v) {
    case "PROCESS_CORRECT":
      return "Process Correct";
    case "BELOW_THRESHOLD":
      return "Below Threshold";
    case "MODEL_ERROR":
      return "Model Error";
    case "VOID":
      return "Void";
    case "WON_NO_DATA":
      return "Won (no data)";
    case "LOST_NO_DATA":
      return "Lost (no data)";
    default:
      return "—";
  }
}

/** Pure: one-line verdict summary for the expanded card head.
 * Format example: "Confidence 74/100 — process correct." */
export function verdictSummary(leg: SlipAutopsyLegRow): string {
  const score = leg.confidence_score;
  const verdict = leg.verdict;
  if (verdict == null) {
    return "Pending verdict.";
  }
  if (verdict === "VOID") {
    return "Void leg — no verdict applied.";
  }
  if (verdict === "WON_NO_DATA" || verdict === "LOST_NO_DATA") {
    return "This fixture was not in STRIKZA's pipeline — verdict not assessed.";
  }
  if (score == null) {
    return `${verdictLabel(verdict)}.`;
  }
  return `Confidence ${Math.round(score)}/100 — ${verdictLabel(verdict).toLowerCase()}.`;
}

/** Pure: did STRIKZA flag this fixture in its pipeline? Surfaces
 * the "STRIKZA's pick" expanded row text. */
export function strikzaPickLine(leg: SlipAutopsyLegRow): string {
  if (leg.was_in_strikza_pipeline && leg.confidence_score != null) {
    return `STRIKZA also flagged this — ${Math.round(leg.confidence_score)}/100.`;
  }
  if (leg.was_in_strikza_pipeline) {
    return "STRIKZA also flagged this fixture.";
  }
  return "STRIKZA did not flag this fixture.";
}

/** Pure: format pattern tags into a single human line. */
export function patternTagsLine(tags: ReadonlyArray<string>): string | null {
  if (!tags || tags.length === 0) return null;
  if (tags.length === 1) {
    return `This adds to your ${humanizeTag(tags[0]!)} pattern.`;
  }
  return `This adds to your ${tags.map(humanizeTag).join(", ")} patterns.`;
}

function humanizeTag(tag: string): string {
  return tag
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Pure: verdict → meta-row tone slug used in the SA-7 CSS. */
export function verdictTone(v: Verdict | null): "green" | "amber" | "red" | "" {
  switch (v) {
    case "PROCESS_CORRECT":
      return "green";
    case "BELOW_THRESHOLD":
      return "amber";
    case "MODEL_ERROR":
      return "red";
    default:
      return "";
  }
}
